import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Mail, CheckCircle2, AlertTriangle, RefreshCw, Trash2, Copy, Check, Globe, ExternalLink, CreditCard, MessageSquare } from "lucide-react";

function CopyButton({ text, testId }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 1600);
      }}
      data-testid={testId}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-white/20 hover:bg-white/30 text-[#f9f8f6]"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
    </button>
  );
}

function EmailDeliverySection({ user }) {
  const [status, setStatus] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/email/status").then((r) => setStatus(r.data));
  useEffect(() => {
    load();
    if (user?.email) setTestTo(user.email);
  }, [user]);

  const sendTest = async () => {
    if (!testTo) return;
    setSending(true);
    try {
      const r = await api.post("/email/test-send", { to: testTo });
      if (r.data.status === "sent") toast.success("Test email delivered via Resend.");
      else if (r.data.status === "mock") toast.warning("No RESEND_API_KEY set — email was logged, not delivered.");
      else if (r.data.status === "failed") toast.error(r.data.error || "Send failed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div id="email-delivery" className="rounded-lg border border-[#e5e2da] bg-white p-6 md:col-span-2 scroll-mt-6" data-testid="settings-email">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#8c9b86]">Email delivery</div>
          <div className="font-serif-display text-2xl mt-1 flex items-center gap-3">
            Resend integration
            {status?.configured ? (
              <Badge className="flex items-center gap-1" style={{ background: "#163325", color: "#f9f8f6", fontWeight: 500 }} data-testid="email-status-configured">
                <CheckCircle2 className="w-3 h-3" /> Live
              </Badge>
            ) : (
              <Badge className="flex items-center gap-1" style={{ background: "#efece5", color: "#5c5c5c", fontWeight: 500 }} data-testid="email-status-mock">
                <AlertTriangle className="w-3 h-3" /> Sandbox (mock)
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className="mt-6 grid sm:grid-cols-4 gap-3 text-center">
        {[
          { label: "Sent", value: status?.sends_sent ?? "–", testId: "email-count-sent" },
          { label: "Scheduled", value: status?.sends_scheduled ?? "–", testId: "email-count-scheduled" },
          { label: "Mocked", value: status?.sends_mocked ?? "–", testId: "email-count-mocked" },
          { label: "Failed", value: status?.sends_failed ?? "–", testId: "email-count-failed" },
        ].map((s) => (
          <div key={s.label} className="rounded-md border border-[#e5e2da] bg-[#f9f8f6] py-3" data-testid={s.testId}>
            <div className="font-serif-display text-3xl text-[#163325]">{s.value}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#5c5c5c] mt-1">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Send test email to</Label>
          <Input
            data-testid="email-test-input"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]"
          />
        </div>
        <Button
          onClick={sendTest}
          disabled={sending || !testTo}
          data-testid="email-test-send-btn"
          className="rounded-md bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6] h-10 px-5"
        >
          <Mail className="w-4 h-4 mr-2" /> {sending ? "Sending…" : "Send test"}
        </Button>
      </div>
    </div>
  );
}

function SenderDomainSection() {
  const [sender, setSender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [domain, setDomain] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/sender");
      setSender(r.data);
      setFromEmail(r.data.from_email || (r.data.domain ? `hello@${r.data.domain}` : ""));
      setFromName(r.data.from_name || "");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addDomain = async () => {
    if (!domain.trim()) return;
    setSaving(true);
    try {
      const r = await api.post("/sender/domain", { domain: domain.trim() });
      setSender((s) => ({ ...(s || {}), ...r.data, configured: true }));
      setDomain("");
      toast.success("Domain added — copy the DNS records below into your registrar, then click Verify.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add domain");
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    setSaving(true);
    try {
      const r = await api.post("/sender/verify");
      setSender((s) => ({ ...(s || {}), ...r.data }));
      if (r.data.status === "verified") toast.success("Domain verified. You can now set your sender address.");
      else toast.warning(`Status: ${r.data.status}. DNS may take up to 48 hours to propagate.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verify failed");
    } finally {
      setSaving(false);
    }
  };

  const saveFrom = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/sender", { from_email: fromEmail, from_name: fromName });
      setSender((s) => ({ ...(s || {}), ...r.data }));
      toast.success("Sender saved. Future nurture emails send from " + fromEmail);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeDomain = async () => {
    if (!confirm("Remove this domain? Future emails will fall back to the sandbox sender.")) return;
    setSaving(true);
    try {
      await api.delete("/sender/domain");
      setSender({ domain: null, status: null, records: [], from_email: null, from_name: null, configured: sender?.configured });
      toast.success("Domain removed.");
    } finally {
      setSaving(false);
    }
  };

  const statusColor = {
    verified: { bg: "#163325", fg: "#f9f8f6" },
    pending: { bg: "#d4a373", fg: "#163325" },
    not_started: { bg: "#efece5", fg: "#5c5c5c" },
    failed: { bg: "#dc2626", fg: "#fff" },
    temporary_failure: { bg: "#dc2626", fg: "#fff" },
  };
  const sc = statusColor[sender?.status] || statusColor.not_started;

  return (
    <div className="rounded-lg border border-[#e5e2da] bg-white p-6 md:col-span-2" data-testid="settings-sender-domain">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#8c9b86]">Sender domain</div>
          <div className="font-serif-display text-2xl mt-1 flex items-center gap-3">
            Verify your domain
            {sender?.status && (
              <Badge className="capitalize" style={{ background: sc.bg, color: sc.fg, fontWeight: 500 }} data-testid="sender-status-badge">
                {sender.status.replace("_", " ")}
              </Badge>
            )}
          </div>
        </div>
        {sender?.domain && (
          <Button variant="ghost" onClick={removeDomain} data-testid="sender-remove-btn" className="text-[#5c5c5c] hover:text-[#dc2626] gap-2">
            <Trash2 className="w-4 h-4" /> Remove
          </Button>
        )}
      </div>

      {loading ? (
        <div className="mt-6 text-sm text-[#5c5c5c]">Loading…</div>
      ) : !sender?.configured ? (
        <div className="mt-6 text-sm text-[#5c5c5c] bg-[#efece5] border border-[#e5e2da] p-4 rounded-md">
          Resend isn't connected on the server yet. Add a <code className="text-xs px-1 bg-white rounded border border-[#e5e2da]">RESEND_API_KEY</code> to <code className="text-xs px-1 bg-white rounded border border-[#e5e2da]">/app/backend/.env</code> to enable domain verification.
        </div>
      ) : !sender?.domain ? (
        <div className="mt-6 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Your domain</Label>
            <div className="mt-1 grid sm:grid-cols-[1fr_auto] gap-3">
              <Input
                data-testid="sender-domain-input"
                placeholder="yourcoachbusiness.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                className="bg-white border-[#e5e2da] focus-visible:ring-[#163325]"
              />
              <Button
                onClick={addDomain}
                disabled={saving || !domain.trim()}
                data-testid="sender-add-btn"
                className="rounded-md bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6] h-10 px-5"
              >
                <Globe className="w-4 h-4 mr-2" /> Add domain
              </Button>
            </div>
          </div>
          <p className="text-xs text-[#5c5c5c]">Enter the bare domain (no https://, no www). We'll ask Resend to generate DNS records, which you'll paste into your registrar.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-sm text-[#5c5c5c]">Domain</div>
              <div className="font-medium text-lg" data-testid="sender-domain-name">{sender.domain}</div>
            </div>
            <Button
              onClick={verify}
              disabled={saving}
              data-testid="sender-verify-btn"
              variant="outline"
              className="border-[#e5e2da] hover:bg-[#efece5] gap-2 rounded-md"
            >
              <RefreshCw className={`w-4 h-4 ${saving ? "animate-spin" : ""}`} /> Re-check status
            </Button>
          </div>

          {sender.status !== "verified" && sender.records?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-[#5c5c5c] mb-2">DNS records to add</div>
              <div className="rounded-md border border-[#e5e2da] overflow-hidden bg-[#f9f8f6]">
                <Table data-testid="sender-dns-records">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent bg-[#efece5]">
                      <TableHead className="w-16">Type</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead className="w-16">TTL</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sender.records.map((r, i) => (
                      <TableRow key={i} className="hover:bg-white">
                        <TableCell className="font-mono text-xs uppercase">{r.type || r.record_type}</TableCell>
                        <TableCell className="font-mono text-xs break-all">{r.name}</TableCell>
                        <TableCell className="font-mono text-xs break-all max-w-md">{r.value}</TableCell>
                        <TableCell className="text-xs text-[#5c5c5c]">{r.ttl || "Auto"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-[#5c5c5c] mt-2">Add these records in your domain registrar (Cloudflare, GoDaddy, Namecheap, etc.). Verification usually takes 5-30 minutes but can be up to 48 hours.</p>
            </div>
          )}

          {sender.status === "verified" && (
            <div className="rounded-md bg-[#163325]/5 border border-[#163325]/20 p-4">
              <div className="text-sm font-medium text-[#163325] flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Verified — set your sender address</div>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">From email</Label>
                  <Input
                    data-testid="sender-from-email-input"
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]"
                    placeholder={`hello@${sender.domain}`}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">From name</Label>
                  <Input
                    data-testid="sender-from-name-input"
                    value={fromName}
                    onChange={(e) => setFromName(e.target.value)}
                    className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]"
                  />
                </div>
              </div>
              <Button
                onClick={saveFrom}
                disabled={saving || !fromEmail}
                data-testid="sender-save-btn"
                className="mt-3 rounded-md bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6]"
              >
                Save sender
              </Button>
              {sender.from_email && (
                <div className="mt-3 text-xs text-[#5c5c5c]">
                  Nurture emails currently send from <b>{sender.from_name} &lt;{sender.from_email}&gt;</b>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InstallWidgetSection({ user }) {
  const [themes, setThemes] = useState([]);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/widget/themes").then((r) => setThemes(r.data.themes));
    if (user) api.get("/widget/theme").then((r) => setActive(r.data)).catch(() => {});
  }, [user]);

  const activeTheme = active || themes.find((t) => t.id === "forest") || {};
  const colorAttr = activeTheme.color ? ` data-color="${activeTheme.color}"` : "";
  const accentAttr = activeTheme.accent ? ` data-accent="${activeTheme.accent}"` : "";
  const snippet = `<script src="${BACKEND_URL}/api/widget.js" data-coach="${user?.id || ""}"${colorAttr}${accentAttr} defer></script>`;

  const pick = async (id) => {
    if (id === active?.theme || saving) return;
    setSaving(id);
    try {
      const r = await api.patch("/widget/theme", { theme: id });
      setActive(r.data);
      toast.success(`Theme set to ${r.data.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save theme");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div id="install-widget" className="rounded-lg border border-[#e5e2da] p-6 md:col-span-2 scroll-mt-6" style={{ background: "#163325", color: "#f9f8f6" }} data-testid="settings-widget">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#d4a373]">Install widget</div>
          <div className="font-serif-display text-2xl mt-1">Paste anywhere — live in 5 minutes</div>
        </div>
        <CopyButton text={snippet} testId="widget-snippet-copy-btn" />
      </div>
      <p className="mt-2 text-sm opacity-80">Drop this script tag into the <code className="text-xs px-1 bg-black/25 rounded">&lt;head&gt;</code> or just before the closing <code className="text-xs px-1 bg-black/25 rounded">&lt;/body&gt;</code> tag on your site. Coachly will start capturing leads and enrolling them in your active email sequence automatically.</p>

      {/* Theme presets */}
      <div className="mt-6" data-testid="widget-theme-picker">
        <div className="text-xs uppercase tracking-widest text-[#d4a373] mb-3">Theme</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {themes.map((t) => {
            const isActive = active?.theme === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => pick(t.id)}
                disabled={!!saving}
                data-testid={`widget-theme-${t.id}`}
                data-active={isActive ? "true" : "false"}
                className={`text-left rounded-lg p-3 border transition-colors ${isActive ? "border-[#d4a373] bg-white/10" : "border-white/15 bg-black/20 hover:bg-black/30"}`}
              >
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full border border-white/20 shrink-0" style={{ background: t.color }} aria-hidden />
                  <span className="w-4 h-4 rounded-full border border-white/20 shrink-0" style={{ background: t.accent }} aria-hidden />
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-sm font-medium capitalize">{t.id}</span>
                  {isActive && <Check className="w-3.5 h-3.5 text-[#d4a373]" />}
                </div>
                <div className="text-[10px] uppercase tracking-widest opacity-60 mt-0.5">{t.label}</div>
                {saving === t.id && <div className="text-[10px] mt-1 opacity-70">Saving…</div>}
              </button>
            );
          })}
        </div>
      </div>

      <code className="block mt-5 text-xs bg-black/25 rounded px-3 py-3 font-mono break-all leading-relaxed" data-testid="widget-snippet-code">{snippet}</code>

      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-xs opacity-80">
        <div>
          <div className="text-[#d4a373] uppercase tracking-widest text-[10px] mb-1">Optional attribute</div>
          <code className="text-xs">data-label="Chat now"</code>
          <div className="mt-1 opacity-75">Override launcher text</div>
        </div>
        <div>
          <div className="text-[#d4a373] uppercase tracking-widest text-[10px] mb-1">Optional attribute</div>
          <code className="text-xs">data-title="Your Brand"</code>
          <div className="mt-1 opacity-75">Chat panel header name</div>
        </div>
      </div>
      <div className="mt-4">
        <a
          href={`/widget-demo.html?coach=${user?.id || ""}&color=${encodeURIComponent(activeTheme.color || "")}&accent=${encodeURIComponent(activeTheme.accent || "")}`}
          target="_blank"
          rel="noreferrer"
          data-testid="widget-preview-link"
          className="text-xs underline underline-offset-4 hover:opacity-80"
        >
          Open live preview page →
        </a>
      </div>
    </div>
  );
}

function BillingSection({ user }) {
  const [busy, setBusy] = useState(false);

  const status = user?.subscription_status;
  const statusLabel = status ? status.replace("_", " ") : "not set";
  const statusColors = {
    trialing: { bg: "#163325", fg: "#f9f8f6", note: "Your trial is running — card will be charged when it ends." },
    active: { bg: "#163325", fg: "#f9f8f6", note: "Subscription is active and paid." },
    past_due: { bg: "#dc2626", fg: "#fff", note: "Payment failed — update your card in the billing portal to avoid interruption." },
    canceled: { bg: "#8c9b86", fg: "#fff", note: "Subscription is canceled. Reactivate anytime from Plans." },
    incomplete: { bg: "#d4a373", fg: "#163325", note: "You haven't finished adding a card yet. Head to Plans to resume checkout." },
    unpaid: { bg: "#dc2626", fg: "#fff", note: "Unpaid invoice — update your card in the billing portal." },
  };
  const sc = statusColors[status] || { bg: "#efece5", fg: "#5c5c5c", note: "No subscription found." };

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/portal", {
        return_url: `${window.location.origin}/app/settings`,
      });
      if (r?.data?.portal_url) window.location.href = r.data.portal_url;
      else throw new Error("Missing portal URL");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal.");
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#e5e2da] bg-white p-6 md:col-span-2" data-testid="settings-billing">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#8c9b86]">Billing</div>
          <div className="font-serif-display text-2xl mt-1 flex items-center gap-3">
            Subscription
            <Badge className="capitalize" style={{ background: sc.bg, color: sc.fg, fontWeight: 500 }} data-testid="billing-status-badge">
              {statusLabel}
            </Badge>
          </div>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-md border border-[#e5e2da] bg-[#f9f8f6] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#5c5c5c]">Plan</div>
          <div className="capitalize mt-1 font-medium" data-testid="billing-plan-value">
            {user?.plan || "—"}
            {user?.billing_interval && (
              <span className="ml-2 text-xs text-[#5c5c5c] font-normal lowercase">
                · {user.billing_interval === "annual" ? "annual" : "monthly"}
              </span>
            )}
          </div>
        </div>
        <div className="rounded-md border border-[#e5e2da] bg-[#f9f8f6] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#5c5c5c]">Status</div>
          <div className="capitalize mt-1 font-medium">{statusLabel}</div>
        </div>
        <div className="rounded-md border border-[#e5e2da] bg-[#f9f8f6] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#5c5c5c]">Payment</div>
          <div className="mt-1 font-medium">Stripe (test mode)</div>
        </div>
      </div>

      <p className="mt-4 text-sm text-[#5c5c5c]" data-testid="billing-note">{sc.note}</p>

      <div className="mt-5 flex flex-wrap gap-3">
        <Button
          onClick={openPortal}
          disabled={busy}
          data-testid="manage-billing-btn"
          className="rounded-md bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6] h-10 px-5 gap-2"
        >
          <CreditCard className="w-4 h-4" />
          {busy ? "Opening…" : "Manage billing"}
          <ExternalLink className="w-3.5 h-3.5 opacity-80" />
        </Button>
        {status === "incomplete" && (
          <a
            href="/plans"
            data-testid="billing-resume-link"
            className="inline-flex items-center gap-2 rounded-md border border-[#163325] text-[#163325] hover:bg-[#efece5] h-10 px-5 text-sm"
          >
            Resume checkout <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
      <div className="mt-3 text-xs text-[#8c9b86]">
        The billing portal is hosted by Stripe. Use it to update your card, download invoices, or cancel your subscription.
      </div>
    </div>
  );
}

function ChatAssistantSection({ user }) {
  const [form, setForm] = useState({ assistant_name: "", welcome_message: "", business_description: "", booking_url: "", lead_alerts: true, daily_digest: true });
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/widget/bot").then((r) => {
      const { defaults: d, ...rest } = r.data;
      setForm(rest);
      setDefaults(d || {});
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/widget/bot", form);
      const { defaults: d, ...rest } = r.data;
      setForm(rest);
      setDefaults(d || {});
      toast.success("Chat assistant updated — live on your site now");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const preview = form.welcome_message.trim() || defaults.welcome_message || "";

  return (
    <div id="chat-assistant" className="rounded-lg border border-[#e5e2da] bg-white p-6 md:col-span-2 scroll-mt-6" data-testid="settings-chat-assistant">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-[#8c9b86]" />
        <div className="text-xs uppercase tracking-widest text-[#8c9b86]">Chat assistant</div>
      </div>
      <div className="font-serif-display text-2xl mt-1">Make it sound like your business</div>
      <p className="mt-2 text-sm text-[#5c5c5c]">The assistant opens every chat with your welcome message and uses your business description to ask visitors relevant questions. Leave a field blank to use the default shown.</p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="bot-assistant-name">Assistant name</Label>
          <Input id="bot-assistant-name" data-testid="bot-assistant-name-input" className="mt-1" value={form.assistant_name} onChange={set("assistant_name")} placeholder={defaults.assistant_name || "e.g. Mia"} maxLength={40} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="bot-welcome">Welcome message</Label>
          <Textarea id="bot-welcome" data-testid="bot-welcome-input" className="mt-1" rows={2} value={form.welcome_message} onChange={set("welcome_message")} placeholder={defaults.welcome_message || ""} maxLength={400} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="bot-description">What you do & who you help</Label>
          <Textarea id="bot-description" data-testid="bot-description-input" className="mt-1" rows={4} value={form.business_description} onChange={set("business_description")} placeholder="e.g. I'm a fitness coach helping busy professionals lose 10–20 lbs with 1:1 online coaching. Typical clients are 30–50, short on time, and want accountability." maxLength={1200} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="bot-booking">Booking link <span className="text-[#8c9b86] font-normal">(Calendly, Cal.com, Acuity…)</span></Label>
          <Input id="bot-booking" data-testid="bot-booking-url-input" className="mt-1" value={form.booking_url} onChange={set("booking_url")} placeholder="https://calendly.com/you/discovery-call" maxLength={300} />
          <p className="mt-1 text-xs text-[#5c5c5c]">Shared the moment a lead is captured and whenever a visitor asks to book — so hot leads can grab a time without waiting for you.</p>
        </div>
        <label className="sm:col-span-2 flex items-start gap-3 rounded-lg border border-[#e5e2da] bg-[#f9f8f6] p-4 cursor-pointer" data-testid="lead-alerts-toggle">
          <input type="checkbox" className="mt-1 accent-[#163325]" checked={!!form.lead_alerts} onChange={(e) => setForm((f) => ({ ...f, lead_alerts: e.target.checked }))} data-testid="lead-alerts-checkbox" />
          <span>
            <span className="block text-sm font-medium">Email me the instant a lead is captured</span>
            <span className="block text-xs text-[#5c5c5c] mt-0.5">Sent to {user?.email} with the lead's name, email and what they're looking for — so you never miss one while away from the dashboard.</span>
          </span>
        </label>
        <label className="sm:col-span-2 flex items-start gap-3 rounded-lg border border-[#e5e2da] bg-[#f9f8f6] p-4 cursor-pointer" data-testid="daily-digest-toggle">
          <input type="checkbox" className="mt-1 accent-[#163325]" checked={!!form.daily_digest} onChange={(e) => setForm((f) => ({ ...f, daily_digest: e.target.checked }))} data-testid="daily-digest-checkbox" />
          <span>
            <span className="block text-sm font-medium">Morning lead digest</span>
            <span className="block text-xs text-[#5c5c5c] mt-0.5">One email at 8am ET summarising yesterday's new leads and who's still waiting on a reply. Skipped on quiet days.</span>
          </span>
        </label>
      </div>

      <div className="mt-5 rounded-lg bg-[#f9f8f6] border border-[#e5e2da] p-4" data-testid="bot-welcome-preview">
        <div className="text-[10px] uppercase tracking-widest text-[#8c9b86] mb-2">Visitors will see</div>
        <div className="inline-block max-w-[85%] rounded-2xl rounded-tl-sm bg-white border border-[#e5e2da] px-4 py-2.5 text-sm">{preview}</div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="bot-settings-save-btn" className="rounded-full bg-[#163325] hover:bg-[#163325]/90 text-white px-6">
          {saving ? "Saving…" : "Save assistant"}
        </Button>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
  }, []);
  return (
    <div className="p-8 lg:p-10" data-testid="dashboard-settings">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#8c9b86]">Account</div>
        <h1 className="font-serif-display text-4xl mt-2">Settings</h1>
      </div>
      <div className="mt-8 grid md:grid-cols-2 gap-4 max-w-5xl">
        <div className="rounded-lg border border-[#e5e2da] bg-white p-6" data-testid="settings-profile">
          <div className="text-xs uppercase tracking-widest text-[#8c9b86]">Profile</div>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-[#5c5c5c]">Name</dt><dd>{user?.name}</dd></div>
            <div className="flex justify-between"><dt className="text-[#5c5c5c]">Email</dt><dd>{user?.email}</dd></div>
            <div className="flex justify-between"><dt className="text-[#5c5c5c]">Business</dt><dd>{user?.business_name || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-[#5c5c5c]">Plan</dt><dd className="capitalize">{user?.plan}</dd></div>
            {user?.utm?.utm_source && (
              <div className="flex justify-between gap-4" data-testid="profile-utm-source"><dt className="text-[#5c5c5c]">Found us via</dt><dd>{user.utm.utm_source}{user.utm.utm_campaign ? ` · ${user.utm.utm_campaign}` : ""}</dd></div>
            )}
            {user?.subscription_status && (
              <div className="flex justify-between"><dt className="text-[#5c5c5c]">Subscription</dt><dd className="capitalize">{user.subscription_status.replace("_", " ")}</dd></div>
            )}
          </dl>
        </div>

        <ChatAssistantSection user={user} />
        <InstallWidgetSection user={user} />
        <BillingSection user={user} />
        <SenderDomainSection />
        <EmailDeliverySection user={user} />
      </div>
    </div>
  );
}
